import React, { useState, useEffect } from 'react'
import { delay, isArray, includes } from 'lodash'
import { State } from 'country-state-list'
import { useI18n } from '@ggs/i18n'
import { Box } from '@mui/system'
import {
  setCurrentCountry,
  useDispatch,
  useSelector,
  setCurrentStateProvince
} from '@ggs/store'
import { SelectInput } from '../../forms/SelectInput/SelectInput'
import { scrollTo } from '@ggs/utils'

/**
 * @typedef {import('../../../definitions').SelectOption} SelectOption
 */

/**
 * Rep locator filters.
 * @return {JSX.Element}
 */
export const RepLocatorFilters = () => {
  const { t } = useI18n()
  const dispatch = useDispatch()
  const selectedCountry = useSelector((state) => state.settings?.country || 'CA')
  const selectedStateProvince = useSelector((state) => state.settings?.stateProvince || '')
  const [countryStates, setCountryStates] = useState([])

  const changeCountry = (/** @type {{ target: { value: string; }; }} */ event) => {
    // Clear the state/province in the redux store after switch country.
    dispatch(setCurrentStateProvince(''))
    // Change the country in the redux store after switch country.
    dispatch(setCurrentCountry(event.target.value))
    // Scroll to the top of the component when it changes
    delay(() => scrollTo.elementById('rep-locator-view'), 200)
  }

  const changeState = (/** @type {{ target: { value: string; }; }} */ event) => {
    const value = event.target.value
    // Set state/province in the redux store.
    if (value !== 'none' && value !== '') {
      dispatch(setCurrentStateProvince(value))
    } else {
      dispatch(setCurrentStateProvince(''))
    }
  }

  const countryOptions = [
    {
      label: t('Canada'),
      value: 'CA',
    },
    {
      label: t('USA'),
      value: 'US',
    },
    {
      label: t('Caribbean'),
      value: 'CAR',
    },
    {
      label: t('Europe'),
      value: 'EU',
    },
    {
      label: t('Asia'),
      value: 'ASI',
    },
    {
      label: t('MENA'),
      value: 'MENA',
    },
  ]

  /**
   * Get state/province options for the selected country.
   * @param {string} countryISO
   * @return {SelectOption[]}
   */
  const getStateOptions = (countryISO) => {
    // Exclude states for the United States
    const excludeStates = [
      'AS',
      'UM-81',
      'GU',
      'UM-84',
      'UM-86',
      'UM-67',
      'UM-89',
      'UM-71',
      'UM-76',
      'MP',
      'UM-95',
      'PR',
      'UM',
      'VI',
      'UM-79',
    ]
    // A single country use the state/province options.
    const countryStates = State.getStatesOfCountry(countryISO)
    return isArray(countryStates)
      ? countryStates.filter((state) => countryISO === 'CA' || (countryISO === 'US' && !includes(excludeStates, state.isoCode)))
        .map((state) => {
          return {
            label: (countryISO === 'US' && state.isoCode === 'DC') ? 'Washington D.C.' : state.name,
            value: state.isoCode,
          }
        })
      : []
  }

  useEffect(() => {
    if (selectedCountry === 'CA' || selectedCountry === 'US') {
      const countryStates = getStateOptions(selectedCountry)
      const sortedCountryStates = countryStates.sort((a, b) => a.label.localeCompare(b.label))
      setCountryStates(sortedCountryStates)
    } else {
      setCountryStates([])
    }
  }, [selectedCountry])

  useEffect(() => {
    // Remove state/province when React mounted and added component to the DOM.
    dispatch(setCurrentStateProvince(''))
    // Set the country to CA when React mounted and added component to the DOM.
    dispatch(setCurrentCountry('CA'))
    return () => {
      // Remove state/province when React unmounted and removed component from the DOM.
      dispatch(setCurrentStateProvince(''))
      // Set the country to CA when React unmounted and removed component from the DOM.
      dispatch(setCurrentCountry('CA'))
    }
  }, [])

  return (
    <div className="rep-locator-filters">
      <div className="form__fields">
        <Box
          className="form-field"
          data-name="country"
          data-input-type="select"
        >
          <SelectInput
            id="country"
            label={t('Country')}
            labelClasses={{
              root: 'form-field__label',
            }}
            placeholder={t('- Select country -')}
            selectOptions={countryOptions}
            allowEmpty={false}
            inputFields={{
              value: selectedCountry,
              // @ts-ignore
              onChange: changeCountry,
              name: 'country',
            }}
          />
        </Box>
        {((selectedCountry === 'CA' || selectedCountry === 'US') && countryStates.length) && (
          <Box
            className="form-field"
            data-name={`state-${selectedCountry}`}
            data-input-type="select"
          >
            <SelectInput
              id={`state-${selectedCountry}`}
              label={t('State/province')}
              labelClasses={{
                root: 'form-field__label',
              }}
              emptyLabel={t('- Select state -')}
              placeholder={t('- Select state -')}
              selectOptions={countryStates}
              allowEmpty={true}
              inputFields={{
                value: selectedStateProvince,
                // @ts-ignore
                onChange: changeState,
                name: `state-${selectedCountry}`,
              }}
            />
          </Box>
        )}
      </div>
    </div>
  )
}
